// src/context/BudgetContext.js - Enhanced with Links Categories Support AND Calculator
import React, { createContext, useContext, useReducer, useEffect } from 'react';
import { createDefaultData } from '../utils/validators';
import { formatCurrency, parseAmount } from '../utils/formatters';
import { getPlannerKey } from '../utils/plannerKeys';
import { ALL_WEEKS } from '../utils/constants';
import {
  getWeekIndexForDate,
  getBudgetMonthYear,
  parseLocalDate
} from '../utils/recurrence';
import { applyDueDateChange, validateDueDate } from '../utils/dueDateChange';
import { getDueTransfers } from '../utils/recurringTransfers';
import {
  getCanonicalPlannerRows,
  sumPlannerWeekTotals,
  getTotalPlannedExpenses as sumTotalPlannedExpenses
} from '../utils/plannerAggregation';

// Selected budget month is UI preference, not budget data: it is persisted
// under its own key so it survives refresh without polluting exports.
const SELECTED_MONTH_STORAGE_KEY = 'family-budget-selected-month';

function loadSelectedBudgetMonth() {
  try {
    const raw = localStorage.getItem(SELECTED_MONTH_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (
      parsed && typeof parsed === 'object' &&
      Number.isInteger(parsed.year) &&
      Number.isInteger(parsed.month) && parsed.month >= 0 && parsed.month <= 11
    ) {
      return { year: parsed.year, month: parsed.month };
    }
  } catch (error) {
    // Corrupt preference — fall back to the derived month.
  }
  return null;
}

const BudgetContext = createContext();

// Normalize accounts coming from imported/saved files so the whole app can rely
// on a single `currentBalance` field. Older exports (and some auto-generated
// account lists) store the balance under `balance`/`availableBalance` and omit
// `currentBalance`, which made imported accounts show $0.00 and appear
// non-editable on the Accounts and Home pages.
function normalizeAccounts(accounts) {
  const list = Array.isArray(accounts)
    ? accounts
    : accounts && typeof accounts === 'object'
      ? Object.values(accounts)
      : [];

  return list.map(account => {
    if (!account || typeof account !== 'object') return account;

    // Preserve an explicit currentBalance (including 0). Fall back to the
    // legacy `balance`, then `availableBalance`, so existing balances survive.
    const hasCurrent = account.currentBalance !== undefined && account.currentBalance !== null && account.currentBalance !== '';
    const fallback = account.balance ?? account.availableBalance ?? 0;
    const currentBalance = hasCurrent ? account.currentBalance : fallback;

    return { ...account, currentBalance: parseFloat(currentBalance) || 0 };
  });
}

// Enhanced action types - Added TOGGLE_CALCULATOR
export const ACTIONS = {
  LOAD_DATA: 'LOAD_DATA',
  UPDATE_DATA: 'UPDATE_DATA',
  UPDATE_INCOME: 'UPDATE_INCOME',
  UPDATE_MONTHLY_EXPENSE: 'UPDATE_MONTHLY_EXPENSE',
  REMOVE_MONTHLY_EXPENSE: 'REMOVE_MONTHLY_EXPENSE',
  ADD_MONTHLY_CATEGORY: 'ADD_MONTHLY_CATEGORY',
  REMOVE_MONTHLY_CATEGORY: 'REMOVE_MONTHLY_CATEGORY',
  REORDER_MONTHLY_EXPENSES: 'REORDER_MONTHLY_EXPENSES',
  UPDATE_ACCOUNTS: 'UPDATE_ACCOUNTS',
  UPDATE_ANNUAL_EXPENSE: 'UPDATE_ANNUAL_EXPENSE',
  REMOVE_ANNUAL_EXPENSE: 'REMOVE_ANNUAL_EXPENSE',
  ADD_ANNUAL_CATEGORY: 'ADD_ANNUAL_CATEGORY',
  REMOVE_ANNUAL_CATEGORY: 'REMOVE_ANNUAL_CATEGORY',
  REORDER_ANNUAL_EXPENSES: 'REORDER_ANNUAL_EXPENSES',
  ADD_ACCOUNT: 'ADD_ACCOUNT',
  UPDATE_ACCOUNT: 'UPDATE_ACCOUNT',
  REMOVE_ACCOUNT: 'REMOVE_ACCOUNT',
  ADD_TRANSFER: 'ADD_TRANSFER',
  DELETE_TRANSFER: 'DELETE_TRANSFER',
  ADD_RECURRING_TRANSFER: 'ADD_RECURRING_TRANSFER',
  UPDATE_RECURRING_TRANSFER: 'UPDATE_RECURRING_TRANSFER',
  REMOVE_RECURRING_TRANSFER: 'REMOVE_RECURRING_TRANSFER',
  POST_RECURRING_TRANSFERS: 'POST_RECURRING_TRANSFERS',
  UPDATE_PLANNER: 'UPDATE_PLANNER',
  UPDATE_PLANNER_WEEKLY_AMOUNT: 'UPDATE_PLANNER_WEEKLY_AMOUNT',
  UPDATE_EXPENSE_STATUS: 'UPDATE_EXPENSE_STATUS',
  SET_CURRENT_PAGE: 'SET_CURRENT_PAGE',
  RESET_DATA: 'RESET_DATA',
  TOGGLE_THEME: 'TOGGLE_THEME',
  TOGGLE_CALCULATOR: 'TOGGLE_CALCULATOR', // Added this action
  UPDATE_LINKS: 'UPDATE_LINKS',
  UPDATE_LINK_CATEGORIES: 'UPDATE_LINK_CATEGORIES',
  ADD_LINK_CATEGORY: 'ADD_LINK_CATEGORY',
  REMOVE_LINK_CATEGORY: 'REMOVE_LINK_CATEGORY',
  MARK_EXPORTED: 'MARK_EXPORTED',
  UPDATE_EXPENSE_DUE_DATE: 'UPDATE_EXPENSE_DUE_DATE',
  SET_BUDGET_MONTH: 'SET_BUDGET_MONTH'
};

// Enhanced initial state - Added isCalculatorOpen
const initialState = {
  data: {
    ...createDefaultData(),
    links: {},
    linkCategories: {
      // Start with some basic categories as examples
      banking: { name: 'Banking & Finance', icon: '🏦', color: '#27ae60', createdDate: new Date().toISOString() },
      utilities: { name: 'Utilities', icon: '⚡', color: '#f39c12', createdDate: new Date().toISOString() },
      government: { name: 'Government', icon: '🏛️', color: '#9b59b6', createdDate: new Date().toISOString() }
    }
  },
  currentPage: 'home',
  theme: 'light',
  isLoading: false,
  lastUpdated: new Date().toISOString(),
  plannerState: {},
  currentWeek: ALL_WEEKS,
  isCalculatorOpen: false, // Added calculator state
  // User-selected budget month ({ year, month } with a 0-based month) or null
  // to derive it from income pay dates. UI preference — never exported.
  selectedBudgetMonth: loadSelectedBudgetMonth(),
  // True when budget data changed since the last JSON export (or import).
  // Drives the "export before you close the tab?" warning. Not part of
  // state.data, so it is never persisted or exported.
  hasUnexportedChanges: false
};

// Enhanced reducer - Added TOGGLE_CALCULATOR case
function budgetReducer(state, action) {
  switch (action.type) {
    case ACTIONS.LOAD_DATA:
      return {
        ...state,
        data: {
          ...state.data,
          ...action.payload,
          // Normalize accounts so balances imported under the legacy
          // `balance`/`availableBalance` schema map onto `currentBalance`.
          accounts: normalizeAccounts(action.payload.accounts),
          // Ensure linkCategories exists
          linkCategories: action.payload.linkCategories || state.data.linkCategories,
          links: action.payload.links || {},
          // Default fallbacks so older exports (without these fields) import cleanly.
          transfers: Array.isArray(action.payload.transfers) ? action.payload.transfers : [],
          recurringTransfers: Array.isArray(action.payload.recurringTransfers)
            ? action.payload.recurringTransfers
            : [],
          plannerState: {
            weekDateRanges: {},
            ...(action.payload.plannerState || state.data.plannerState || {})
          }
        },
        lastUpdated: new Date().toISOString()
      };

    case ACTIONS.UPDATE_DATA:
      return {
        ...state,
        data: {
          ...state.data,
          ...action.payload
        },
        lastUpdated: new Date().toISOString()
      };

    case ACTIONS.UPDATE_LINKS:
      return {
        ...state,
        data: {
          ...state.data,
          links: action.payload
        },
        lastUpdated: new Date().toISOString()
      };

    case ACTIONS.UPDATE_LINK_CATEGORIES:
      return {
        ...state,
        data: {
          ...state.data,
          linkCategories: action.payload
        },
        lastUpdated: new Date().toISOString()
      };

    case ACTIONS.ADD_LINK_CATEGORY:
      return {
        ...state,
        data: {
          ...state.data,
          linkCategories: {
            ...state.data.linkCategories,
            [action.categoryKey]: {
              ...action.payload,
              createdDate: new Date().toISOString()
            }
          }
        },
        lastUpdated: new Date().toISOString()
      };

    case ACTIONS.REMOVE_LINK_CATEGORY:
      const { [action.categoryKey]: removed, ...remainingCategories } = state.data.linkCategories;
      const { [action.categoryKey]: removedLinks, ...remainingLinks } = state.data.links;
      return {
        ...state,
        data: {
          ...state.data,
          linkCategories: remainingCategories,
          links: remainingLinks
        },
        lastUpdated: new Date().toISOString()
      };

    case ACTIONS.UPDATE_INCOME:
      return {
        ...state,
        data: {
          ...state.data,
          income: action.payload
        },
        lastUpdated: new Date().toISOString()
      };

    case ACTIONS.UPDATE_MONTHLY_EXPENSE:
      const monthlyCategory = state.data.monthly[action.category] || [];
      const updatedMonthlyCategory = [...monthlyCategory];

      if (action.index >= 0 && action.index < updatedMonthlyCategory.length) {
        updatedMonthlyCategory[action.index] = action.payload;
      } else {
        updatedMonthlyCategory.push(action.payload);
      }

      return {
        ...state,
        data: {
          ...state.data,
          monthly: {
            ...state.data.monthly,
            [action.category]: updatedMonthlyCategory
          }
        },
        lastUpdated: new Date().toISOString()
      };

    case ACTIONS.REMOVE_MONTHLY_EXPENSE:
      const monthlyExpenses = state.data.monthly[action.category] || [];
      const filteredMonthlyExpenses = monthlyExpenses.filter((_, i) => i !== action.index);

      return {
        ...state,
        data: {
          ...state.data,
          monthly: {
            ...state.data.monthly,
            [action.category]: filteredMonthlyExpenses
          }
        },
        lastUpdated: new Date().toISOString()
      };

    case ACTIONS.REORDER_MONTHLY_EXPENSES:
      return {
        ...state,
        data: {
          ...state.data,
          monthly: {
            ...state.data.monthly,
            [action.payload.categoryKey]: action.payload.reorderedExpenses
          }
        },
        lastUpdated: new Date().toISOString()
      };

    case ACTIONS.ADD_MONTHLY_CATEGORY: {
      const newMonthly = { ...state.data.monthly };
      if (!newMonthly[action.categoryKey]) {
        newMonthly[action.categoryKey] = [];
      }
      return {
        ...state,
        data: {
          ...state.data,
          monthly: newMonthly
        },
        lastUpdated: new Date().toISOString()
      };
    }

    case ACTIONS.REMOVE_MONTHLY_CATEGORY: {
      const newMonthly = { ...state.data.monthly };
      delete newMonthly[action.categoryKey];
      return {
        ...state,
        data: {
          ...state.data,
          monthly: newMonthly
        },
        lastUpdated: new Date().toISOString()
      };
    }

    case ACTIONS.UPDATE_ANNUAL_EXPENSE:
      const annualCategory = state.data.annual[action.category] || [];
      const updatedAnnualCategory = [...annualCategory];

      if (action.index >= 0 && action.index < updatedAnnualCategory.length) {
        updatedAnnualCategory[action.index] = action.payload;
      } else {
        updatedAnnualCategory.push(action.payload);
      }

      return {
        ...state,
        data: {
          ...state.data,
          annual: {
            ...state.data.annual,
            [action.category]: updatedAnnualCategory
          }
        },
        lastUpdated: new Date().toISOString()
      };

    case ACTIONS.REMOVE_ANNUAL_EXPENSE:
      const annualExpenses = state.data.annual[action.category] || [];
      const filteredAnnualExpenses = annualExpenses.filter((_, i) => i !== action.index);

      return {
        ...state,
        data: {
          ...state.data,
          annual: {
            ...state.data.annual,
            [action.category]: filteredAnnualExpenses
          }
        },
        lastUpdated: new Date().toISOString()
      };

    case ACTIONS.REORDER_ANNUAL_EXPENSES:
      return {
        ...state,
        data: {
          ...state.data,
          annual: {
            ...state.data.annual,
            [action.payload.categoryKey]: action.payload.reorderedExpenses
          }
        },
        lastUpdated: new Date().toISOString()
      };

    case ACTIONS.ADD_ANNUAL_CATEGORY: {
      const newAnnual = { ...state.data.annual };
      if (!newAnnual[action.categoryKey]) {
        newAnnual[action.categoryKey] = [];
      }
      return {
        ...state,
        data: {
          ...state.data,
          annual: newAnnual
        },
        lastUpdated: new Date().toISOString()
      };
    }

    case ACTIONS.REMOVE_ANNUAL_CATEGORY: {
      const newAnnual = { ...state.data.annual };
      delete newAnnual[action.categoryKey];
      return {
        ...state,
        data: {
          ...state.data,
          annual: newAnnual
        },
        lastUpdated: new Date().toISOString()
      };
    }

    case ACTIONS.UPDATE_ACCOUNTS: {
      const accounts = Array.isArray(action.payload) ? [...action.payload] : [];
      return {
        ...state,
        data: { ...state.data, accounts },
        lastUpdated: new Date().toISOString()
      };
    }

    case ACTIONS.ADD_ACCOUNT:
      const accounts = Array.isArray(state.data.accounts) ? [...state.data.accounts] : [];
      accounts.push(action.payload);
      return {
        ...state,
        data: { ...state.data, accounts },
        lastUpdated: new Date().toISOString()
      };

    case ACTIONS.UPDATE_ACCOUNT:
      const updatedAccounts = Array.isArray(state.data.accounts)
        ? state.data.accounts.map(acc => acc.id === action.payload.id ? { ...acc, ...action.payload } : acc)
        : [];
      return {
        ...state,
        data: { ...state.data, accounts: updatedAccounts },
        lastUpdated: new Date().toISOString()
      };

    case ACTIONS.REMOVE_ACCOUNT:
      const filteredAccounts = Array.isArray(state.data.accounts)
        ? state.data.accounts.filter(acc => acc.id !== action.id)
        : [];
      return {
        ...state,
        data: { ...state.data, accounts: filteredAccounts },
        lastUpdated: new Date().toISOString()
      };

    // Move money between two accounts. Balances are adjusted atomically and the
    // transfer is recorded in the ledger. Both `currentBalance` and the legacy
    // `balance` field are kept in sync so imports/exports stay compatible.
    case ACTIONS.ADD_TRANSFER: {
      const transfer = action.payload;
      const amount = parseFloat(transfer.amount) || 0;
      const accountsList = Array.isArray(state.data.accounts) ? state.data.accounts : [];

      const adjustedAccounts = accountsList.map(acc => {
        const current = parseFloat(acc.currentBalance ?? acc.balance) || 0;
        if (acc.id === transfer.fromAccountId) {
          const next = current - amount;
          return { ...acc, currentBalance: next, balance: next };
        }
        if (acc.id === transfer.toAccountId) {
          const next = current + amount;
          return { ...acc, currentBalance: next, balance: next };
        }
        return acc;
      });

      const transfers = [transfer, ...(Array.isArray(state.data.transfers) ? state.data.transfers : [])];
      return {
        ...state,
        data: { ...state.data, accounts: adjustedAccounts, transfers },
        lastUpdated: new Date().toISOString()
      };
    }

    // Undo a transfer: reverse the balance changes (add back to source, subtract
    // from destination) and drop it from the ledger.
    case ACTIONS.DELETE_TRANSFER: {
      const transfers = Array.isArray(state.data.transfers) ? state.data.transfers : [];
      const transfer = transfers.find(t => t.id === action.id);
      if (!transfer) {
        return state;
      }
      const amount = parseFloat(transfer.amount) || 0;
      const accountsList = Array.isArray(state.data.accounts) ? state.data.accounts : [];

      // Only a full round-trip is safe. If either referenced account has since
      // been deleted, reversing just the surviving side would corrupt its
      // balance (it legitimately holds/lost the money), so drop the stale ledger
      // entry WITHOUT touching any balances. The UI also disables Undo in this
      // case; this guard is defense-in-depth.
      const bothAccountsExist =
        accountsList.some(a => a.id === transfer.fromAccountId) &&
        accountsList.some(a => a.id === transfer.toAccountId);

      const revertedAccounts = bothAccountsExist
        ? accountsList.map(acc => {
            const current = parseFloat(acc.currentBalance ?? acc.balance) || 0;
            if (acc.id === transfer.fromAccountId) {
              const next = current + amount;
              return { ...acc, currentBalance: next, balance: next };
            }
            if (acc.id === transfer.toAccountId) {
              const next = current - amount;
              return { ...acc, currentBalance: next, balance: next };
            }
            return acc;
          })
        : accountsList;

      // If the transfer was auto-posted by a recurring schedule, remember the
      // occurrence date as skipped — otherwise the posting effect would see
      // it missing from the ledger and immediately re-post it, silently
      // undoing the user's undo.
      const recurringTransfers = (transfer.recurringTransferId && Array.isArray(state.data.recurringTransfers))
        ? state.data.recurringTransfers.map(s =>
            s.id === transfer.recurringTransferId
              ? {
                  ...s,
                  skippedDates: [
                    ...(Array.isArray(s.skippedDates) ? s.skippedDates : []),
                    transfer.date
                  ]
                }
              : s)
        : state.data.recurringTransfers;

      return {
        ...state,
        data: {
          ...state.data,
          accounts: revertedAccounts,
          transfers: transfers.filter(t => t.id !== action.id),
          recurringTransfers
        },
        lastUpdated: new Date().toISOString()
      };
    }

    // ----- Recurring transfer SCHEDULES (data.recurringTransfers) -----
    // Schedules only describe future generation; the transfers they produce
    // are ordinary ledger entries posted via POST_RECURRING_TRANSFERS below,
    // so pausing/editing/deleting a schedule never rewrites history.
    case ACTIONS.ADD_RECURRING_TRANSFER: {
      const schedules = Array.isArray(state.data.recurringTransfers)
        ? [...state.data.recurringTransfers]
        : [];
      schedules.push(action.payload);
      return {
        ...state,
        data: { ...state.data, recurringTransfers: schedules },
        lastUpdated: new Date().toISOString()
      };
    }

    case ACTIONS.UPDATE_RECURRING_TRANSFER: {
      const schedules = Array.isArray(state.data.recurringTransfers)
        ? state.data.recurringTransfers.map(s =>
            s.id === action.payload.id ? { ...s, ...action.payload } : s)
        : [];
      return {
        ...state,
        data: { ...state.data, recurringTransfers: schedules },
        lastUpdated: new Date().toISOString()
      };
    }

    case ACTIONS.REMOVE_RECURRING_TRANSFER: {
      const schedules = Array.isArray(state.data.recurringTransfers)
        ? state.data.recurringTransfers.filter(s => s.id !== action.id)
        : [];
      return {
        ...state,
        data: { ...state.data, recurringTransfers: schedules },
        lastUpdated: new Date().toISOString()
      };
    }

    // Post a batch of due recurring-transfer occurrences to the ledger,
    // adjusting both account balances exactly like a manual ADD_TRANSFER.
    // Occurrence ids are deterministic (rt-<scheduleId>-<date>), and any id
    // already in the ledger is skipped here as a final guard, so a batch can
    // never double-post or double-adjust balances.
    case ACTIONS.POST_RECURRING_TRANSFERS: {
      const batch = Array.isArray(action.payload) ? action.payload : [];
      if (batch.length === 0) return state;

      const existingTransfers = Array.isArray(state.data.transfers) ? state.data.transfers : [];
      const existingIds = new Set(existingTransfers.map(t => t.id));
      const toPost = batch.filter(t => t && t.id && !existingIds.has(t.id));
      if (toPost.length === 0) return state;

      let adjustedAccounts = Array.isArray(state.data.accounts) ? state.data.accounts : [];
      toPost.forEach(transfer => {
        const amount = parseFloat(transfer.amount) || 0;
        adjustedAccounts = adjustedAccounts.map(acc => {
          const current = parseFloat(acc.currentBalance ?? acc.balance) || 0;
          if (acc.id === transfer.fromAccountId) {
            const next = current - amount;
            return { ...acc, currentBalance: next, balance: next };
          }
          if (acc.id === transfer.toAccountId) {
            const next = current + amount;
            return { ...acc, currentBalance: next, balance: next };
          }
          return acc;
        });
      });

      // Newest first, matching how manual transfers are prepended.
      const transfers = [...toPost].reverse().concat(existingTransfers);
      return {
        ...state,
        data: { ...state.data, accounts: adjustedAccounts, transfers },
        lastUpdated: new Date().toISOString()
      };
    }

    case ACTIONS.UPDATE_PLANNER: {
      const incoming = action.payload || {};
      const existing = state.data.plannerState || {};
      const plannerState = { ...existing, ...incoming };

      return {
        ...state,
        data: { ...state.data, plannerState },
        lastUpdated: new Date().toISOString()
      };
    }

    case ACTIONS.UPDATE_PLANNER_WEEKLY_AMOUNT: {
      const { expenseName, weekIndex, value } = action.payload;
      const planner = { ...(state.data.plannerState || {}) };
      const expense = planner[expenseName] || {
        weeks: Array(5).fill(0),
        transferred: Array(5).fill(false),
        paid: Array(5).fill(false)
      };
      // FIXED: Ensure weeks array always has exactly 5 elements
      let weeks = Array.isArray(expense.weeks) ? [...expense.weeks] : Array(5).fill(0);
      while (weeks.length < 5) weeks.push(0);
      weeks = weeks.slice(0, 5);

      if (weekIndex >= 0 && weekIndex < 5) {
        weeks[weekIndex] = value;
      }
      planner[expenseName] = { ...expense, weeks };
      return {
        ...state,
        data: { ...state.data, plannerState: planner },
        lastUpdated: new Date().toISOString()
      };
    }

    // Single code path for editing an expense's due date (Planner, Monthly
    // and Annual pages all dispatch this). Persists the date on the
    // authoritative record and re-homes the Planner's week amount/status —
    // see utils/dueDateChange.js.
    case ACTIONS.UPDATE_EXPENSE_DUE_DATE: {
      const { descriptor, newDate } = action.payload;
      const result = applyDueDateChange(state.data, descriptor, newDate);
      if (!result.changed) return state;
      return {
        ...state,
        data: result.data,
        lastUpdated: new Date().toISOString()
      };
    }

    case ACTIONS.SET_BUDGET_MONTH:
      return {
        ...state,
        selectedBudgetMonth: action.payload
      };

    case ACTIONS.SET_CURRENT_PAGE:
      return {
        ...state,
        currentPage: action.payload
      };

    case ACTIONS.RESET_DATA:
      return {
        ...initialState,
        data: createDefaultData()
      };

    case ACTIONS.TOGGLE_THEME:
      return {
        ...state,
        theme: state.theme === 'light' ? 'dark' : 'light'
      };

    // Added TOGGLE_CALCULATOR case
    case ACTIONS.TOGGLE_CALCULATOR:
      return {
        ...state,
        isCalculatorOpen: !state.isCalculatorOpen
      };

    default:
      return state;
  }
}

// Boot and import fire automatic planner rebuilds (autoPopulatePlanner) moments
// after LOAD_DATA; data changes inside this grace window are system-generated,
// not user edits, and must not flag the session as "unexported".
let dirtyGraceUntil = Date.now() + 2500;

// Wraps budgetReducer to maintain hasUnexportedChanges: any user action that
// produces a new state.data marks the session dirty; a successful JSON export
// (MARK_EXPORTED) or a load/import/reset clears it.
function budgetReducerWithExportTracking(state, action) {
  const next = budgetReducer(state, action);

  if (action.type === ACTIONS.MARK_EXPORTED) {
    return { ...next, hasUnexportedChanges: false };
  }

  if (action.type === ACTIONS.LOAD_DATA || action.type === ACTIONS.RESET_DATA) {
    dirtyGraceUntil = Date.now() + 2500;
    return { ...next, hasUnexportedChanges: false };
  }

  if (next.data !== state.data && !state.hasUnexportedChanges && Date.now() >= dirtyGraceUntil) {
    return { ...next, hasUnexportedChanges: true };
  }

  return next;
}

// Context Provider Component
export function BudgetProvider({ children }) {
  const [state, dispatch] = useReducer(budgetReducerWithExportTracking, initialState);

  // Guard against the boot-time save race: without this, the save effect's
  // first run wrote the DEFAULT data over the user's saved budget before
  // LOAD_DATA had applied, leaving a brief window where closing the tab (or
  // a crash) lost real data.
  const skipFirstSaveRef = React.useRef(true);

  // Load data from localStorage on mount
  useEffect(() => {
    try {
      const savedData = localStorage.getItem('family-budget-data');
      if (savedData) {
        const parsedData = JSON.parse(savedData);
        if (parsedData && typeof parsedData === 'object') {
          dispatch({ type: ACTIONS.LOAD_DATA, payload: parsedData });
        }
      }
    } catch (error) {
      // Silently handle errors in production
      if (process.env.NODE_ENV === 'development') {
        console.error('Failed to load saved data:', error);
      }
    }
  }, []);

  // Save data to localStorage whenever state changes (skipping the initial
  // mount so the default state never clobbers saved data before it loads).
  useEffect(() => {
    if (skipFirstSaveRef.current) {
      skipFirstSaveRef.current = false;
      return;
    }
    try {
      localStorage.setItem('family-budget-data', JSON.stringify(state.data));
    } catch (error) {
      // Silently handle errors in production
      if (process.env.NODE_ENV === 'development') {
        console.error('Failed to save data:', error);
      }
    }
  }, [state.data]);

  // Persist the selected budget month (UI preference) across refreshes.
  useEffect(() => {
    try {
      if (state.selectedBudgetMonth) {
        localStorage.setItem(SELECTED_MONTH_STORAGE_KEY, JSON.stringify(state.selectedBudgetMonth));
      } else {
        localStorage.removeItem(SELECTED_MONTH_STORAGE_KEY);
      }
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Failed to save selected budget month:', error);
      }
    }
  }, [state.selectedBudgetMonth]);

  // Warn before the tab/browser closes while there are budget changes that
  // haven't been exported to a JSON backup. Browsers only permit their own
  // built-in confirm dialog here (custom text/buttons are ignored for
  // security): "Leave" closes the tab, "Cancel" keeps it open so the user
  // can export first. Data itself is still safe in localStorage either way.
  useEffect(() => {
    const handleBeforeUnload = (event) => {
      if (!state.hasUnexportedChanges) return undefined;
      event.preventDefault();
      // Legacy channel for the prompt; modern browsers show generic wording.
      event.returnValue = 'You have budget changes that have not been exported. Close anyway?';
      return event.returnValue;
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [state.hasUnexportedChanges]);

  // Enhanced action creators - Added toggleCalculator
  const actions = {
    loadData: (data) => {
      dispatch({ type: ACTIONS.LOAD_DATA, payload: data });
    },

    // Called after a successful JSON export: the on-disk backup now matches
    // the app data, so the close-tab warning can stand down.
    markExported: () => {
      dispatch({ type: ACTIONS.MARK_EXPORTED });
    },

    updateData: (data) => {
      dispatch({ type: ACTIONS.UPDATE_DATA, payload: data });
    },

    updateIncome: (income) => {
      dispatch({ type: ACTIONS.UPDATE_INCOME, payload: income });
    },

    updateMonthlyExpense: (category, expense, index) => {
      dispatch({ type: ACTIONS.UPDATE_MONTHLY_EXPENSE, payload: expense, category, index });
    },

    removeMonthlyExpense: (category, index) => {
      dispatch({ type: ACTIONS.REMOVE_MONTHLY_EXPENSE, category, index });
    },

    reorderMonthlyExpenses: (categoryKey, reorderedExpenses) => {
      dispatch({
        type: ACTIONS.REORDER_MONTHLY_EXPENSES,
        payload: { categoryKey, reorderedExpenses }
      });
    },

    addMonthlyCategory: (categoryKey) => {
      dispatch({ type: ACTIONS.ADD_MONTHLY_CATEGORY, categoryKey });
    },

    removeMonthlyCategory: (categoryKey) => {
      dispatch({ type: ACTIONS.REMOVE_MONTHLY_CATEGORY, categoryKey });
    },

    updateAnnualExpense: (category, expense, index) => {
      dispatch({ type: ACTIONS.UPDATE_ANNUAL_EXPENSE, payload: expense, category, index });
    },

    removeAnnualExpense: (category, index) => {
      dispatch({ type: ACTIONS.REMOVE_ANNUAL_EXPENSE, category, index });
    },

    reorderAnnualExpenses: (categoryKey, reorderedExpenses) => {
      dispatch({
        type: ACTIONS.REORDER_ANNUAL_EXPENSES,
        payload: { categoryKey, reorderedExpenses }
      });
    },

    addAnnualCategory: (categoryKey) => {
      dispatch({ type: ACTIONS.ADD_ANNUAL_CATEGORY, categoryKey });
    },

    removeAnnualCategory: (categoryKey) => {
      dispatch({ type: ACTIONS.REMOVE_ANNUAL_CATEGORY, categoryKey });
    },

    updateAccounts: (accounts) => {
      dispatch({ type: ACTIONS.UPDATE_ACCOUNTS, payload: accounts });
    },

    addAccount: (account) => {
      dispatch({ type: ACTIONS.ADD_ACCOUNT, payload: account });
    },

    updateAccount: (account) => {
      dispatch({ type: ACTIONS.UPDATE_ACCOUNT, payload: account });
    },

    removeAccount: (id) => {
      dispatch({ type: ACTIONS.REMOVE_ACCOUNT, id });
    },

    // Record a money transfer between two accounts. Fills in id/createdAt if the
    // caller didn't provide them.
    addTransfer: (transfer) => {
      const payload = {
        id: transfer.id || `transfer-${Date.now()}`,
        createdAt: transfer.createdAt || new Date().toISOString(),
        fromAccountId: transfer.fromAccountId,
        toAccountId: transfer.toAccountId,
        amount: parseFloat(transfer.amount) || 0,
        date: transfer.date || new Date().toISOString().slice(0, 10),
        note: transfer.note || ''
      };
      dispatch({ type: ACTIONS.ADD_TRANSFER, payload });
    },

    deleteTransfer: (id) => {
      dispatch({ type: ACTIONS.DELETE_TRANSFER, id });
    },

    // Create a recurring transfer schedule. Fills in id/createdAt when the
    // caller didn't provide them. The schedule's due occurrences are posted
    // automatically (see the effect in BudgetProvider).
    addRecurringTransfer: (schedule) => {
      const payload = {
        id: schedule.id || `rtransfer-${Date.now()}`,
        createdAt: schedule.createdAt || new Date().toISOString(),
        fromAccountId: schedule.fromAccountId,
        toAccountId: schedule.toAccountId,
        amount: parseFloat(schedule.amount) || 0,
        note: schedule.note || '',
        frequency: schedule.frequency || 'monthly',
        startDate: schedule.startDate || new Date().toISOString().slice(0, 10),
        endDate: schedule.endDate || '',
        maxOccurrences: schedule.maxOccurrences || '',
        paused: !!schedule.paused
      };
      dispatch({ type: ACTIONS.ADD_RECURRING_TRANSFER, payload });
    },

    // Edit a schedule (also used to pause/resume via the `paused` flag).
    // Only affects FUTURE occurrences — transfers already posted to the
    // ledger are history and stay untouched.
    updateRecurringTransfer: (schedule) => {
      dispatch({ type: ACTIONS.UPDATE_RECURRING_TRANSFER, payload: schedule });
    },

    removeRecurringTransfer: (id) => {
      dispatch({ type: ACTIONS.REMOVE_RECURRING_TRANSFER, id });
    },

    updatePlanner: (plannerData) => {
      dispatch({ type: ACTIONS.UPDATE_PLANNER, payload: plannerData });
    },

    // Set (or clear) the Planner-only payment account override for an expense.
    // Pass an empty accountId to clear the override and fall back to the
    // expense's original accountId. This never touches the Monthly/Annual data.
    setPlannerAccountOverride: (expense, accountId) => {
      const plannerKey = getPlannerKey(expense);
      const legacyKey = expense?.name;
      const planner = state.data.plannerState || {};
      const existingEntry = planner[plannerKey]
        || (legacyKey ? planner[legacyKey] : undefined)
        || { weeks: Array(5).fill(0), transferred: Array(5).fill(false), paid: Array(5).fill(false) };

      const updatedEntry = { ...existingEntry };
      if (accountId) {
        updatedEntry.plannerAccountId = accountId;
      } else {
        delete updatedEntry.plannerAccountId;
      }

      const payload = { [plannerKey]: updatedEntry };
      if (legacyKey && legacyKey !== plannerKey) {
        payload[legacyKey] = updatedEntry;
      }
      dispatch({ type: ACTIONS.UPDATE_PLANNER, payload });
    },

    updatePlannerWeeklyAmount: (expenseOrKey, weekIndex, value) => {
      const expenseKey = typeof expenseOrKey === 'string'
        ? expenseOrKey
        : getPlannerKey(expenseOrKey);
      const legacyName = typeof expenseOrKey === 'object' ? expenseOrKey?.name : expenseOrKey;

      dispatch({
        type: ACTIONS.UPDATE_PLANNER_WEEKLY_AMOUNT,
        payload: {
          expenseName: expenseKey,
          legacyName,
          weekIndex,
          value: parseFloat(value) || 0
        }
      });
    },

    setCurrentPage: (page) => {
      dispatch({ type: ACTIONS.SET_CURRENT_PAGE, payload: page });
    },

    // Change the budget month every date-aware view works against.
    // Pass { year, month } (0-based month) or null to fall back to the
    // month derived from income pay dates.
    setBudgetMonth: (budgetMonth) => {
      dispatch({ type: ACTIONS.SET_BUDGET_MONTH, payload: budgetMonth });
    },

    // Edit an expense's due date through the single authoritative code path.
    // descriptor: { collection: 'monthly'|'annual', categoryKey, index, id, name }
    // Returns { ok: true } on success or { ok: false, error } when the date
    // is invalid, so callers can surface the failure instead of silently
    // dropping the edit.
    updateExpenseDueDate: (descriptor, newDate) => {
      const validation = validateDueDate(newDate);
      if (!validation.ok) return validation;
      dispatch({
        type: ACTIONS.UPDATE_EXPENSE_DUE_DATE,
        payload: { descriptor, newDate: newDate || '' }
      });
      return { ok: true };
    },

    resetData: () => {
      dispatch({ type: ACTIONS.RESET_DATA });
    },

    toggleTheme: () => {
      dispatch({ type: ACTIONS.TOGGLE_THEME });
    },

    // Added toggleCalculator action
    toggleCalculator: () => {
      dispatch({ type: ACTIONS.TOGGLE_CALCULATOR });
    },

    // Enhanced Links Actions
    updateLinks: (links) => {
      dispatch({ type: ACTIONS.UPDATE_LINKS, payload: links });
    },

    updateLinkCategories: (categories) => {
      dispatch({ type: ACTIONS.UPDATE_LINK_CATEGORIES, payload: categories });
    },

    addLinkCategory: (categoryKey, categoryData) => {
      dispatch({ type: ACTIONS.ADD_LINK_CATEGORY, categoryKey, payload: categoryData });
    },

    removeLinkCategory: (categoryKey) => {
      dispatch({ type: ACTIONS.REMOVE_LINK_CATEGORY, categoryKey });
    },

    // Convenience methods for links
    addLink: (categoryKey, link) => {
      const currentLinks = state.data.links || {};
      const categoryLinks = currentLinks[categoryKey] || [];

      const newLink = {
        ...link,
        id: link.id || `link-${Date.now()}`,
        addedDate: link.addedDate || new Date().toISOString()
      };

      const updatedLinks = {
        ...currentLinks,
        [categoryKey]: [...categoryLinks, newLink]
      };

      dispatch({ type: ACTIONS.UPDATE_LINKS, payload: updatedLinks });
    },

    updateLink: (categoryKey, linkId, updatedLink) => {
      const currentLinks = state.data.links || {};
      const categoryLinks = currentLinks[categoryKey] || [];

      const updatedCategoryLinks = categoryLinks.map(link =>
        link.id === linkId ? { ...link, ...updatedLink } : link
      );

      const updatedLinks = {
        ...currentLinks,
        [categoryKey]: updatedCategoryLinks
      };

      dispatch({ type: ACTIONS.UPDATE_LINKS, payload: updatedLinks });
    },

    removeLink: (categoryKey, linkId) => {
      const currentLinks = state.data.links || {};
      const categoryLinks = currentLinks[categoryKey] || [];

      const updatedCategoryLinks = categoryLinks.filter(link => link.id !== linkId);

      const updatedLinks = {
        ...currentLinks,
        [categoryKey]: updatedCategoryLinks
      };

      dispatch({ type: ACTIONS.UPDATE_LINKS, payload: updatedLinks });
    },

    // Planner-specific actions.
    // Accepts either the full expense object (preferred — resolves the same
    // canonical `id:`-prefixed planner key the Planner page reads) or a bare
    // name string for backward compatibility. Name-only lookups used to write
    // under a key the Planner never read, so distributed amounts silently
    // failed to appear in the Planner's week cells.
    distributeMonthlyToWeekly: (expenseOrName, monthlyAmount, dueDate) => {
      const expense = typeof expenseOrName === 'string'
        ? { name: expenseOrName }
        : (expenseOrName || {});
      const expenseName = expense.name;
      const plannerKey = getPlannerKey(expense);
      const existingData = state.data.plannerState[plannerKey]
        || (expenseName ? state.data.plannerState[expenseName] : undefined)
        || {
          weeks: Array(5).fill(0),
          transferred: Array(5).fill(false),
          paid: Array(5).fill(false)
        };

      if (dueDate) {
        // Shared week rule (utils/recurrence.js) — same bucketing as the
        // Weekly page and the Planner.
        const targetWeek = getWeekIndexForDate(dueDate);
        if (targetWeek === null) return;

        // Write the WHOLE distribution (full amount in the due week, $0
        // elsewhere). Setting only the due week used to leave stale amounts
        // in other weeks after an amount edit, so the Planner's totals no
        // longer matched the Monthly tab.
        const newWeeks = Array(5).fill(0);
        newWeeks[targetWeek] = monthlyAmount;

        const updatedData = {
          ...existingData,
          weeks: newWeeks
        };

        const newPlannerState = {
          ...state.data.plannerState,
          [plannerKey]: updatedData,
          // Keep the plain-name alias in sync for legacy lookups.
          ...(expenseName && plannerKey !== expenseName ? { [expenseName]: updatedData } : {})
        };

        dispatch({ type: ACTIONS.UPDATE_PLANNER, payload: newPlannerState });
      }
    },

    autoPopulatePlanner: () => {
      const existingPlanner = state.data.plannerState || {};

      const newPlannerState = {
        ...existingPlanner,
        weeklyIncome: Array(5).fill(0),
        weeklyExpenses: Array(5).fill(0),
        monthlyTargets: {}
      };

      const addToWeeklyExpenses = (amounts = []) => {
        amounts.slice(0, 5).forEach((amt, idx) => {
          newPlannerState.weeklyExpenses[idx] =
            (newPlannerState.weeklyExpenses[idx] || 0) + (parseFloat(amt) || 0);
        });
      };

      const activeExpenseNames = new Set();
      const autoDistributeFlags = new Map();

      const registerExpenseKeys = (expense) => {
        const expenseKey = getPlannerKey(expense);
        if (expense?.name) {
          activeExpenseNames.add(expense.name);
        }
        activeExpenseNames.add(expenseKey);
        return expenseKey;
      };

      const markAutoDistributeFlag = (expense, shouldAutoDistribute) => {
        const expenseKey = getPlannerKey(expense);
        autoDistributeFlags.set(expenseKey, shouldAutoDistribute);
        if (expense?.name) {
          autoDistributeFlags.set(expense.name, shouldAutoDistribute);
        }
      };

      const getExistingEntry = (expense) => {
        const expenseKey = getPlannerKey(expense);
        return existingPlanner[expenseKey] || (expense?.name ? existingPlanner[expense.name] : undefined);
      };

      const writePlannerEntry = (expense, plannerEntry) => {
        const expenseKey = getPlannerKey(expense);
        // Carry over the Planner-only account override (if any) so rebuilding the
        // planner rows never loses the user's per-row payment-account choice.
        const existingEntry = existingPlanner[expenseKey]
          || (expense?.name ? existingPlanner[expense.name] : undefined);
        const entry = (existingEntry && existingEntry.plannerAccountId !== undefined)
          ? { ...plannerEntry, plannerAccountId: existingEntry.plannerAccountId }
          : plannerEntry;
        newPlannerState[expenseKey] = entry;
        if (expense?.name && expenseKey !== expense.name) {
          newPlannerState[expense.name] = entry;
        }
      };

      const clampWeeksArray = (weeks = []) => {
        if (!Array.isArray(weeks)) return Array(5).fill(0);
        const arr = weeks.slice(0, 5).map(val => parseFloat(val) || 0);
        // FIXED: Ensure array always has exactly 5 elements by padding with zeros
        while (arr.length < 5) arr.push(0);
        return arr;
      };

      const clampBoolArray = (boolArr = []) => {
        if (!Array.isArray(boolArr)) return Array(5).fill(false);
        const arr = boolArr.slice(0, 5).map(val => Boolean(val));
        // Ensure array always has exactly 5 elements by padding with false
        while (arr.length < 5) arr.push(false);
        return arr;
      };

      const buildManualPlannerEntry = (expense, monthlyAmount) => {
        const existingEntry = getExistingEntry(expense);
        const existingWeeks = clampWeeksArray(existingEntry?.weeks);

        // FIXED: Always preserve existing weeks for manually-managed expenses
        // Previously, this would reset weeks to zeros if they matched auto-distribution,
        // causing user-entered values to be lost when switching pages
        const weeks = existingWeeks;

        return {
          weeks,
          transferred: clampBoolArray(existingEntry?.transferred),
          paid: clampBoolArray(existingEntry?.paid)
        };
      };

      // Process income sources (evenly distributed across weeks)
      if (Array.isArray(state.data.income)) {
        state.data.income.forEach(income => {
          const amount = parseFloat(
            income.actualAmount || income.amount || 0
          );
          if (!isNaN(amount) && amount > 0) {
            const weekly = amount / 5;
            for (let i = 0; i < 5; i++) {
              newPlannerState.weeklyIncome[i] += weekly;
            }
          }
        });
      }

      const addExpense = (expense, monthlyAmount) => {
        if (!(expense.name && expense.name.trim())) return;
        if (isNaN(monthlyAmount) || monthlyAmount <= 0) return;

        registerExpenseKeys(expense);

        const existingEntry = getExistingEntry(expense);
        // FIXED: Use clampWeeksArray to ensure preserved weeks always has exactly 5 elements
        const preservedWeeks = existingEntry?.weeks
          ? clampWeeksArray(existingEntry.weeks)
          : null;

        let weeklyAmounts = Array(5).fill(0);
        // Timezone-safe week placement: new Date('YYYY-MM-DD') parses as UTC
        // and lands one day early in Canadian timezones, which used to put
        // e.g. a 15th-of-month bill in Week 2 here while the Weekly page
        // showed it in Week 3. getWeekIndexForDate is the shared rule.
        const dueWeek = getWeekIndexForDate(expense.date);
        if (dueWeek !== null) {
          weeklyAmounts[dueWeek] = monthlyAmount;
        } else {
          const weeklyAmount = monthlyAmount / 5;
          weeklyAmounts = Array(5).fill(weeklyAmount);
        }

        const differsFromAuto = preservedWeeks?.some((val, idx) =>
          Math.abs((val || 0) - (weeklyAmounts[idx] || 0)) > 0.0001
        );
        const hasUserValue = preservedWeeks?.some((val) => Math.abs(val || 0) > 0.0001);

        const plannerEntry = {
          weeks: (differsFromAuto || hasUserValue) ? preservedWeeks : weeklyAmounts,
          transferred: existingEntry?.transferred
            ? clampBoolArray(existingEntry.transferred)
            : Array(5).fill(expense.transferred || false),
          paid: existingEntry?.paid
            ? clampBoolArray(existingEntry.paid)
            : Array(5).fill(expense.paid || false)
        };

        writePlannerEntry(expense, plannerEntry);

        addToWeeklyExpenses(plannerEntry.weeks);
      };

      // Process monthly expenses
      Object.entries(state.data.monthly || {}).forEach(([categoryKey, expenses]) => {
        (expenses || []).forEach(expense => {
          const expenseWithCategory = { ...expense, categoryKey };

          registerExpenseKeys(expenseWithCategory);

          const shouldAutoDistribute = expenseWithCategory?.autoDistributeToPlanner === true;
          if (expenseWithCategory?.name) {
            markAutoDistributeFlag(expenseWithCategory, shouldAutoDistribute);
          }

          const monthlyAmount = parseFloat(expenseWithCategory.actual || expenseWithCategory.amount || 0);

          if (shouldAutoDistribute) {
            addExpense(expenseWithCategory, monthlyAmount);
          } else if (expenseWithCategory?.name && expenseWithCategory.name.trim()) {
            const manualEntry = buildManualPlannerEntry(expenseWithCategory, monthlyAmount || 0);
            writePlannerEntry(expenseWithCategory, manualEntry);
            addToWeeklyExpenses(manualEntry.weeks);
          }
        });
      });

      // Process annual expenses (convert to monthly equivalent)
      Object.entries(state.data.annual || {}).forEach(([categoryKey, expenses]) => {
        (expenses || []).forEach(expense => {
          const expenseWithCategory = { ...expense, categoryKey };

          if (expenseWithCategory?.name && expenseWithCategory.name.trim()) {
            registerExpenseKeys(expenseWithCategory);
          }

          const annualAmount = parseFloat(expenseWithCategory.actual || expenseWithCategory.amount || 0);
          const plannerAmount = expenseWithCategory.annualPaymentMode === 'none'
            ? 0
            : expenseWithCategory.annualPaymentMode === 'full'
              ? annualAmount
              : annualAmount / 12;
          addExpense(expenseWithCategory, plannerAmount);
        });
      });

      // Preserve existing planner entries for active expenses that are manually managed
      Object.entries(existingPlanner).forEach(([expenseName, entry]) => {
        if (['weeklyIncome', 'weeklyExpenses', 'monthlyTargets', 'weekDateRanges'].includes(expenseName)) {
          return;
        }

        if (!activeExpenseNames.has(expenseName)) {
          return;
        }

        if (!newPlannerState[expenseName]) {
          const manualManaged = autoDistributeFlags.get(expenseName) === false;
          const weeks = clampWeeksArray(entry.weeks);

          newPlannerState[expenseName] = {
            weeks,
            transferred: clampBoolArray(entry.transferred),
            paid: clampBoolArray(entry.paid),
            // Preserve the Planner-only account override across rebuilds.
            ...(entry.plannerAccountId !== undefined ? { plannerAccountId: entry.plannerAccountId } : {})
          };

          addToWeeklyExpenses(weeks);
        }
      });

      // weekDateRanges is user-edited data, not derived from expenses. Never let
      // an auto-populate pass (which can run with a stale state snapshot on mount)
      // overwrite it — omit it so UPDATE_PLANNER's merge keeps the persisted value.
      delete newPlannerState.weekDateRanges;

      dispatch({ type: ACTIONS.UPDATE_PLANNER, payload: newPlannerState });
    },

    updateExpenseStatus: (expenseId, expenseName, weekIndex, statusType, checked, _sourceModule) => {
      let updatedMonthly = { ...state.data.monthly };
      let updatedAnnual = { ...state.data.annual };
      let updatedPlanner = { ...state.data.plannerState };

      const plannerKey = getPlannerKey({ id: expenseId, name: expenseName });
      const plannerExpense = updatedPlanner[plannerKey] || updatedPlanner[expenseName];

      if (plannerExpense) {
        const nextPlanner = { ...plannerExpense };
        if (!nextPlanner[statusType]) {
          nextPlanner[statusType] = Array(5).fill(false);
        }
        nextPlanner[statusType] = [...nextPlanner[statusType]];
        if (weekIndex === null || weekIndex === undefined || weekIndex < 0) {
          nextPlanner[statusType] = nextPlanner[statusType].map(() => checked);
        } else {
          nextPlanner[statusType][weekIndex] = checked;
        }
        updatedPlanner[plannerKey] = nextPlanner;
        if (plannerKey !== expenseName) {
          updatedPlanner[expenseName] = nextPlanner;
        }
      }

      // Update matching monthly and annual expenses
      if (expenseId) {
        Object.keys(updatedMonthly).forEach(categoryKey => {
          const categoryExpenses = updatedMonthly[categoryKey] || [];
          const expenseIndex = categoryExpenses.findIndex(exp => exp.id === expenseId);
          if (expenseIndex !== -1) {
            updatedMonthly[categoryKey][expenseIndex] = {
              ...categoryExpenses[expenseIndex],
              [statusType]: checked
            };
          }
        });

        Object.keys(updatedAnnual).forEach(categoryKey => {
          const categoryExpenses = updatedAnnual[categoryKey] || [];
          const expenseIndex = categoryExpenses.findIndex(exp => exp.id === expenseId);
          if (expenseIndex !== -1) {
            updatedAnnual[categoryKey][expenseIndex] = {
              ...categoryExpenses[expenseIndex],
              [statusType]: checked
            };
          }
        });
      }

      // Update all relevant data
      dispatch({
        type: ACTIONS.UPDATE_DATA, payload: {
          ...state.data,
          monthly: updatedMonthly,
          annual: updatedAnnual,
          plannerState: updatedPlanner
        }
      });
    },
  };

  // Enhanced calculations
  const calculations = {
    getTotalIncome: () => {
      if (!Array.isArray(state.data.income)) return 0;
      return state.data.income.reduce((total, source) => {
        return total + (parseFloat(source.amount) || 0);
      }, 0);
    },

    getTotalMonthlyExpenses: () => {
      let total = 0;
      Object.values(state.data.monthly || {}).forEach(category => {
        if (Array.isArray(category)) {
          category.forEach(expense => {
            total += parseFloat(expense.actual || expense.amount || 0);
          });
        }
      });
      return total;
    },

    getTotalAnnualExpenses: () => {
      let total = 0;
      Object.values(state.data.annual || {}).forEach(category => {
        if (Array.isArray(category)) {
          category.forEach(expense => {
            total += parseFloat(expense.actual || expense.amount || 0);
          });
        }
      });
      return total;
    },

    getMonthlyBalance: () => {
      const income = calculations.getTotalIncome();
      const monthlyExpenses = calculations.getTotalMonthlyExpenses();
      const annualMonthly = calculations.getTotalAnnualExpenses() / 12;
      return income - monthlyExpenses - annualMonthly;
    },

    getNetMonthlyIncome: () => {
      return calculations.getMonthlyBalance();
    },

    getAnnualSavings: () => {
      return calculations.getMonthlyBalance() * 12;
    },

    getCashFlow: () => {
      const totalIncome = calculations.getTotalIncome();
      const totalExpenses = calculations.getTotalMonthlyExpenses() + (calculations.getTotalAnnualExpenses() / 12);
      return {
        income: totalIncome,
        expenses: totalExpenses,
        netIncome: totalIncome - totalExpenses,
        savingsRate: totalIncome > 0 ? ((totalIncome - totalExpenses) / totalIncome) * 100 : 0
      };
    },

    getSavingsRate: () => {
      const totalIncome = calculations.getTotalIncome();
      const totalExpenses = calculations.getTotalMonthlyExpenses() + (calculations.getTotalAnnualExpenses() / 12);
      if (totalIncome <= 0) return 0;
      return ((totalIncome - totalExpenses) / totalIncome) * 100;
    },

    getUpcomingExpenses: (daysAhead = 30) => {
      const upcoming = [];
      const today = new Date();

      const processExpenses = (collection, type) => {
        Object.entries(collection || {}).forEach(([categoryKey, expenses]) => {
          if (Array.isArray(expenses)) {
            expenses.forEach(expense => {
              if (expense.date && !expense.paid) {
                const dueDate = parseLocalDate(expense.date) || new Date(expense.date);
                const daysUntil = Math.ceil((dueDate - today) / (24 * 60 * 60 * 1000));
                if (daysUntil >= 0 && daysUntil <= daysAhead) {
                  upcoming.push({
                    ...expense,
                    category: categoryKey,
                    type,
                    daysUntil,
                    amount: parseAmount(expense.actual || expense.amount || 0)
                  });
                }
              }
            });
          }
        });
      };

      processExpenses(state.data.monthly, 'monthly');
      processExpenses(state.data.annual, 'annual');

      return upcoming.sort((a, b) => a.daysUntil - b.daysUntil);
    },

    getOverdueExpenses: () => {
      const overdue = [];
      const today = new Date();

      // Check monthly expenses
      Object.entries(state.data.monthly || {}).forEach(([categoryKey, expenses]) => {
        expenses.forEach(expense => {
          if (expense.date && expense.name && !expense.paid) {
            const dueDate = parseLocalDate(expense.date) || new Date(expense.date);
            if (dueDate < today) {
              overdue.push({
                ...expense,
                category: categoryKey,
                type: 'monthly',
                daysOverdue: Math.ceil((today - dueDate) / (24 * 60 * 60 * 1000))
              });
            }
          }
        });
      });

      // Check annual expenses
      Object.entries(state.data.annual || {}).forEach(([categoryKey, expenses]) => {
        expenses.forEach(expense => {
          if (expense.date && expense.name && !expense.paid) {
            const dueDate = parseLocalDate(expense.date) || new Date(expense.date);
            if (dueDate < today) {
              overdue.push({
                ...expense,
                category: categoryKey,
                type: 'annual',
                daysOverdue: Math.ceil((today - dueDate) / (24 * 60 * 60 * 1000))
              });
            }
          }
        });
      });

      return overdue.sort((a, b) => b.daysOverdue - a.daysOverdue);
    },

    getExpensesByStatus: (status = 'unpaid') => {
      const expenses = [];

      // Check monthly expenses
      Object.entries(state.data.monthly || {}).forEach(([categoryKey, categoryExpenses]) => {
        categoryExpenses.forEach(expense => {
          if (expense.name) {
            const isPaid = expense.paid || false;
            const isTransferred = expense.transferred || false;

            if (status === 'paid' && isPaid) {
              expenses.push({ ...expense, category: categoryKey, type: 'monthly' });
            } else if (status === 'transferred' && isTransferred) {
              expenses.push({ ...expense, category: categoryKey, type: 'monthly' });
            } else if (status === 'unpaid' && !isPaid) {
              expenses.push({ ...expense, category: categoryKey, type: 'monthly' });
            }
          }
        });
      });

      // Check annual expenses
      Object.entries(state.data.annual || {}).forEach(([categoryKey, categoryExpenses]) => {
        categoryExpenses.forEach(expense => {
          if (expense.name) {
            const isPaid = expense.paid || false;
            const isTransferred = expense.transferred || false;

            if (status === 'paid' && isPaid) {
              expenses.push({ ...expense, category: categoryKey, type: 'annual' });
            } else if (status === 'transferred' && isTransferred) {
              expenses.push({ ...expense, category: categoryKey, type: 'annual' });
            } else if (status === 'unpaid' && !isPaid) {
              expenses.push({ ...expense, category: categoryKey, type: 'annual' });
            }
          }
        });
      });

      return expenses;
    },

    // NOTE: planner entries live under BOTH an `id:`-prefixed key and a
    // plain-name alias, so these aggregates go through the canonical helpers
    // in utils/plannerAggregation.js (one row per real expense) instead of
    // iterating plannerState directly — the old direct iteration counted
    // every expense twice.
    getWeeklyPlannerTotals: () => {
      const weeklyIncome = calculations.getWeeklyIncome();
      const rows = getCanonicalPlannerRows(state.data);
      const weekTotals = sumPlannerWeekTotals(state.data);

      // Return detailed weekly information
      return weekTotals.map((weekExpenses, index) => ({
        week: index + 1,
        income: weeklyIncome[index] || 0,
        expenses: weekExpenses,
        balance: (weeklyIncome[index] || 0) - weekExpenses,
        expenseCount: rows.filter(({ entry }) =>
          entry?.weeks && parseFloat(entry.weeks[index]) > 0
        ).length
      }));
    },

    getPlannerExpensesByWeek: (weekIndex) => {
      if (weekIndex < 0 || weekIndex >= 5) return [];

      const expenses = [];
      getCanonicalPlannerRows(state.data).forEach(({ expense, entry }) => {
        const amount = parseFloat(entry?.weeks?.[weekIndex]) || 0;
        if (amount > 0) {
          expenses.push({
            name: expense.name,
            amount: amount,
            paid: entry.paid ? entry.paid[weekIndex] : false,
            transferred: entry.transferred ? entry.transferred[weekIndex] : false,
            week: weekIndex + 1
          });
        }
      });

      return expenses.sort((a, b) => b.amount - a.amount);
    },

    getTotalPlannedIncome: () => {
      return calculations.getWeeklyIncome().reduce((total, weekIncome) => total + weekIncome, 0);
    },

    getTotalPlannedExpenses: () => {
      return sumTotalPlannedExpenses(state.data);
    },

    getPlannerBalance: () => {
      return calculations.getTotalPlannedIncome() - calculations.getTotalPlannedExpenses();
    },

    getCategoryTotal: (categoryKey, isAnnual = false) => {
      const data = isAnnual ? state.data.annual : state.data.monthly;
      const category = data?.[categoryKey] || [];
      return category.reduce((total, expense) => {
        return total + (parseFloat(expense.actual || expense.amount || 0));
      }, 0);
    },

    getMonthlyAnnualImpact: () => {
      return calculations.getTotalAnnualExpenses() / 12;
    },

    getExpenseById: (expenseId) => {
      // Search in monthly expenses
      for (const [categoryKey, expenses] of Object.entries(state.data.monthly || {})) {
        const expense = expenses.find(exp => exp.id === expenseId);
        if (expense) return { ...expense, category: categoryKey, type: 'monthly' };
      }

      // Search in annual expenses
      for (const [categoryKey, expenses] of Object.entries(state.data.annual || {})) {
        const expense = expenses.find(exp => exp.id === expenseId);
        if (expense) return { ...expense, category: categoryKey, type: 'annual' };
      }

      return null;
    },

    getWeeklyIncome: () => {
      const totalIncome = calculations.getTotalIncome();
      return Array(5).fill(totalIncome / 4.33); // Average weeks per month
    },

    getTotalPlannedForWeek: (weekIndex) => {
      if (weekIndex < 0 || weekIndex >= 5) return 0;
      return sumPlannerWeekTotals(state.data)[weekIndex];
    },

    getWeeklyBalance: (weekIndex) => {
      const income = calculations.getWeeklyIncome()[weekIndex] || 0;
      const expenses = calculations.getTotalPlannedForWeek(weekIndex);
      return income - expenses;
    },

    // Links calculations
    getTotalLinksCount: () => {
      return Object.values(state.data.links || {}).reduce((total, categoryLinks) => {
        return total + (Array.isArray(categoryLinks) ? categoryLinks.length : 0);
      }, 0);
    },

    getLinksCategoriesCount: () => {
      return Object.keys(state.data.linkCategories || {}).length;
    },

    getLargestLinkCategory: () => {
      let largest = { name: '', count: 0 };
      Object.entries(state.data.links || {}).forEach(([categoryKey, links]) => {
        const count = Array.isArray(links) ? links.length : 0;
        if (count > largest.count) {
          const categoryInfo = state.data.linkCategories?.[categoryKey];
          largest = {
            name: categoryInfo?.name || categoryKey,
            count
          };
        }
      });
      return largest;
    }
  };

  // The budget month every date-aware view works against: the user's
  // explicit selection, otherwise derived from income pay dates (falling
  // back to today). { year, month } with a 0-based month.
  const budgetMonth = state.selectedBudgetMonth || getBudgetMonthYear(state.data.income);

  const contextValue = {
    state,
    actions,
    calculations,
    formatCurrency,
    parseAmount,
    budgetMonth
  };

  // Keep planner rows aligned with monthly/annual changes (including auto-distribute toggles)
  useEffect(() => {
    actions.autoPopulatePlanner();
  }, [state.data.monthly, state.data.annual]);

  // Post due recurring-transfer occurrences to the ledger. Runs whenever the
  // schedules, ledger or accounts change; deterministic occurrence ids make
  // this idempotent (already-posted dates are skipped), so once everything
  // due has been posted the effect settles with no further dispatches.
  useEffect(() => {
    const due = getDueTransfers(
      state.data.recurringTransfers,
      state.data.transfers,
      state.data.accounts
    );
    if (due.length > 0) {
      dispatch({ type: ACTIONS.POST_RECURRING_TRANSFERS, payload: due });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.data.recurringTransfers, state.data.transfers, state.data.accounts]);

  return (
    <BudgetContext.Provider value={contextValue}>
      {children}
    </BudgetContext.Provider>
  );
}

// Custom hook to use the Budget context
export function useBudget() {
  const context = useContext(BudgetContext);
  if (!context) {
    throw new Error('useBudget must be used within a BudgetProvider');
  }
  return context;
}